import { makeStyles } from '@material-ui/core/styles'
import { Theme, Box, Grid, ButtonBase, Typography } from '@material-ui/core'
import React from 'react'
import { FinalSelection } from '../utils'
import QuoteItem from './QuoteItem'
import { useCurrentUser } from '@src/utils/useCurrentUser'
import LiquidQuantityInput from '@src/components/LiquidQuantityInput'
import EditAndCloseButtons from '../Step2/SearchResult/ProductCard/EditAndCloseButtons'
import CloseIcon from '@material-ui/icons/Close'
import { FormattedMessage } from 'react-intl'

const useStyles = makeStyles((theme: Theme) => ({
  productCardContainer: {
    border: `solid ${theme.palette.grey[500]} 1px`,
    borderRadius: theme.spacing(1.5),
    marginBottom: theme.spacing(6),
  },
  alignRight: {
    textAlign: 'right',
    textWrap: 'nowrap',
  },
  productTitle: {
    padding: theme.spacing(4, 6),
    backgroundColor: theme.palette.background.grey,
    fontWeight: theme.typography.fontWeightBold,
    width: '100%',
    borderRadius: theme.spacing(1.5, 1.5, 0, 0),
    borderBottom: `solid ${theme.palette.grey[500]} 1px`,
    fontSize: theme.typography.pxToRem(16),
  },
  deleteCloseIcon: {
    fontSize: theme.typography.pxToRem(22),
    cursor: 'pointer',
    fontWeight: theme.typography.fontWeightBold,
  },
  columnTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  columnTitleContainer: {
    width: theme.spacing(43),
    display: 'inline-block',
  },
  topSectionLayout: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    padding: theme.spacing(6),
  },
  rightSectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: theme.spacing(75),
    gap: '8px',
  },
  quantityPickerWrapper: {
    height: 'fit-content',
    width: theme.spacing(26),
  },
  editButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: theme.spacing(6),
    marginBottom: theme.spacing(6),
  },
  analytesContainer: {
    padding: theme.spacing(2, 6),
    width: '90%',
  },
  quoteItemWrapper: {
    height: 'fit-content',
    width: theme.spacing(30),
    display: 'flex',
    justifyContent: 'center',
  },
}))

interface ProductCardProps {
  productData: FinalSelection
  handleQuantityChange: (quantity: number, id: string) => void
  handleEdit: (productNo: string) => void
  handleDeleteItem: (productNo: string) => void
}
const StepThreeProductCard: React.FC<ProductCardProps> = ({
  productData,
  handleQuantityChange,
  handleEdit,
  handleDeleteItem,
}) => {
  const classes = useStyles()
  const { isDealer, currentUser } = useCurrentUser()
  const isAuUser =
    currentUser?.__typename === 'LoggedInUser' &&
    currentUser.metadata?.country &&
    currentUser.metadata?.country === 'AU'

  return (
    <Grid container className={classes.productCardContainer}>
      <Grid item className={classes.productTitle}>
        {productData?.productName}
      </Grid>
      <Grid item xs={12}>
        <Box className={classes.topSectionLayout}>
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box className={classes.columnTitleContainer}>
                  <Typography
                    display="inline"
                    variant="body2"
                    className={classes.columnTitle}
                  >
                    <FormattedMessage
                      id="PRODUCT_NO_V2"
                      defaultMessage="Product No"
                    />
                    :
                  </Typography>
                </Box>

                <Typography variant="body2" display="inline">
                  {productData?.productNo}
                </Typography>
              </Grid>

              {productData?.sampleType ? (
                <Grid item xs={12}>
                  <Box className={classes.columnTitleContainer}>
                    <Typography
                      variant="body2"
                      display="inline"
                      className={classes.columnTitle}
                    >
                      <FormattedMessage
                        id="SAMPLE_TYPE"
                        defaultMessage="Sample Type"
                      />
                      :
                    </Typography>
                  </Box>
                  <Typography variant="body2" display="inline">
                    {productData?.sampleType}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}

              {productData?.customPremixing ? (
                <Grid item xs={12}>
                  <Box className={classes.columnTitleContainer}>
                    <Typography
                      variant="body2"
                      display="inline"
                      className={classes.columnTitle}
                    >
                      <FormattedMessage
                        id="CONF_MILLIPLEX_CUSTOM_PREMIX_LABEL_milliplex"
                        defaultMessage="Custom Premixing"
                      />
                      :
                    </Typography>
                  </Box>

                  <Typography variant="body2" display="inline">
                    {productData?.customPremixing}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}

              {productData?.spaceSaving ? (
                <Grid item xs={12}>
                  <Box className={classes.columnTitleContainer}>
                    <Typography
                      variant="body2"
                      display="inline"
                      className={classes.columnTitle}
                    >
                      <FormattedMessage
                        id="SPACE_SAVING"
                        defaultMessage="Space Saving"
                      />
                      :
                    </Typography>
                  </Box>

                  <Typography variant="body2" display="inline">
                    {productData?.spaceSaving}
                  </Typography>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </Box>
          <Box className={classes.rightSectionContainer}>
            <Box className={classes.quantityPickerWrapper}>
              <LiquidQuantityInput
                value={productData?.quantity}
                min={1}
                onChange={(value) => {
                  handleQuantityChange(value, productData?.productNo)
                }}
              />
            </Box>
            <Box className={classes.quoteItemWrapper}>
              <QuoteItem
                isDealer={isDealer}
                isAuUser={isAuUser}
                row={productData}
              />
            </Box>

            <Box
              sx={{
                height: 'fit-content',
              }}
            >
              {' '}
              <ButtonBase
                onClick={() => handleDeleteItem(productData?.productNo)}
              >
                <CloseIcon className={classes.deleteCloseIcon} />
              </ButtonBase>
            </Box>
          </Box>
        </Box>
        <Grid item sm={12} style={{ marginLeft: '24px' }}>
          <Typography className={classes.columnTitle} variant="body2">
            <FormattedMessage
              id="MILLIPLEX_ANALYTES_IN_KIT"
              defaultMessage="Analytes in Kit"
            />
            &nbsp;{`(${productData.analytesInKit.length}-plex)`}:
          </Typography>
        </Grid>
        <Grid container spacing={2} className={classes.analytesContainer}>
          {productData.analytesInKit
            .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            .map((analyte, index) => (
              <Grid item xs={4} key={index}>
                {analyte}
              </Grid>
            ))}
        </Grid>
        <Box className={classes.editButtonWrapper}>
          <EditAndCloseButtons
            onClick={() => handleEdit(productData?.productNo)}
          />
        </Box>
      </Grid>
    </Grid>
  )
}

export default StepThreeProductCard
